import { Category, getStaticCategory } from '@/api/models';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { CategoryFormData } from '@/pages/categories/hooks/useCategoryMutations';
import { mdiDelete, mdiPencil } from '@mdi/js';
import * as React from 'react';
import { CategoryForm } from './CategoryForm';

export interface CategoryDrawerViewProps {
  // State
  isOpen: boolean;
  category: Category | null;
  mode: 'view' | 'create' | 'edit';

  // Loading
  isLoading: boolean;
  isFormLoading: boolean;

  // Actions
  onClose: () => void;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
  onFormSubmit: (data: CategoryFormData) => Promise<void>;
}

export const CategoryDrawerView: React.FC<CategoryDrawerViewProps> = ({
  // State
  isOpen,
  category,
  mode,

  // Loading
  isLoading,
  isFormLoading,

  // Actions
  onClose,
  onEdit,
  onDelete,
  onFormSubmit,
}) => {
  const staticCategory = category ? getStaticCategory(category.name) : null;

  const getTitle = () => {
    switch (mode) {
      case 'create':
        return 'Create New Category';
      case 'edit':
        return category
          ? `Edit ${staticCategory?.label || category.name}`
          : 'Edit Category';
      case 'view':
      default:
        return category
          ? staticCategory?.label || category.name
          : 'Category Details';
    }
  };

  if (isLoading) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} title='Loading...' width='md'>
        <div className='p-6 flex items-center justify-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={getTitle()} width='md'>
      {mode === 'view' && category && (
        <div className='p-6 space-y-6'>
          {/* Category Header */}
          <div className='flex items-center space-x-4 pb-6 border-b border-gray-200'>
            <div className='w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-4xl'>
              {staticCategory?.emoji || '📁'}
            </div>
            <div className='flex-1'>
              <h3 className='text-xl font-semibold text-gray-900'>
                {staticCategory?.label || category.name}
              </h3>
              <div className='text-sm text-gray-500'>{category.name}</div>
              <div className='mt-2 flex items-center space-x-2'>
                {category.enabled ? (
                  <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700'>
                    Enabled
                  </span>
                ) : (
                  <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700'>
                    Disabled
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Category Details */}
          <div className='grid grid-cols-1 gap-6'>
            <div className='bg-gray-50 rounded-lg p-4'>
              <h4 className='text-sm font-medium text-gray-700 mb-3'>
                Category Information
              </h4>
              <dl className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <dt className='text-xs text-gray-500'>Category ID</dt>
                  <dd className='text-sm font-mono text-gray-900'>
                    {category.id}
                  </dd>
                </div>
                <div>
                  <dt className='text-xs text-gray-500'>Businesses Using</dt>
                  <dd className='text-sm text-gray-900'>
                    <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700'>
                      {category.businessCount ?? 0}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className='text-xs text-gray-500'>Created</dt>
                  <dd className='text-sm text-gray-900'>
                    {category.createdAt
                      ? new Date(category.createdAt).toLocaleDateString(
                          'en-US',
                          {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          },
                        )
                      : 'N/A'}
                  </dd>
                </div>
                {category.updatedAt && (
                  <div>
                    <dt className='text-xs text-gray-500'>Last Updated</dt>
                    <dd className='text-sm text-gray-900'>
                      {new Date(category.updatedAt).toLocaleDateString(
                        'en-US',
                        {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        },
                      )}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Quick Actions */}
            <div className='bg-gray-50 rounded-lg p-4'>
              <h4 className='text-sm font-medium text-gray-700 mb-3'>
                Quick Actions
              </h4>
              <div className='flex flex-wrap gap-2'>
                <Button
                  icon={mdiPencil}
                  label='Edit Category'
                  color='info'
                  small
                  onClick={() => onEdit(category)}
                />
                <Button
                  icon={mdiDelete}
                  label='Delete Category'
                  color='danger'
                  small
                  outline
                  onClick={() => onDelete(category)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {(mode === 'create' || mode === 'edit') && (
        <CategoryForm
          category={category}
          onSubmit={onFormSubmit}
          onCancel={onClose}
          loading={isFormLoading}
        />
      )}
    </Drawer>
  );
};
